import type { AudienceMode } from "./audienceMode";
import type { AwsRegionId } from "./awsRegion";
import type { InputMode } from "./inputMode";
import type { LlmPriceProfile } from "./llmPriceProfile";
import type { ScenarioSnapshot } from "./scenario";
import type { AddonInfraConfig } from "./addonInfra";
import type { DeploymentStackConfig } from "./deploymentStack";
import type { SavingsLevers } from "./savingsLevers";
import type { DisplayCurrency } from "./displayCurrency";
import type { ComputeProfileId } from "./computeProfile";
import type { CostPerformanceMode, Workload } from "./types";

/** Як показувати головний total поруч із помісячними рядками. */
export type CostDisplayUnit = "month" | "year" | "perDau" | "perMillionReq";

const STORAGE_KEY = "cost-analyzer:ui:v1";

export type PersistedUi = {
  inputMode?: InputMode;
  input: string;
  /** Базовий workload (обидва режими). */
  workload?: Workload;
  userMult: number;
  reqMult: number;
  cpMode: CostPerformanceMode;
  awsRegion?: AwsRegionId;
  /** Monthly budget cap in USD; empty / omitted = no cap. */
  budgetCapUsd?: number | null;
  checklist?: Record<string, boolean>;
  scenarioA?: ScenarioSnapshot;
  scenarioB?: ScenarioSnapshot;
  savings?: SavingsLevers;
  /** Legacy; read only for migration into deploymentStack. */
  addonInfra?: AddonInfraConfig;
  deploymentStack?: DeploymentStackConfig;
  llmPriceProfile?: LlmPriceProfile;
  /** CPU-bound API vs heavier compute heuristic (Lambda / egress multipliers). */
  computeProfile?: ComputeProfileId;
  /** Фактичний чек (USD/міс), рядок з поля вводу. */
  actualBillStr?: string;
  costDisplayUnit?: CostDisplayUnit;
  audienceMode?: AudienceMode;
  /** Нотатки до сценарію (локально, у JSON експорті). */
  scenarioNotes?: string;
  /** Показ сум у EUR за індикативним курсом; модель лишається USD. */
  displayCurrency?: DisplayCurrency;
  /** Останній імпорт Cost Explorer: сервіс → USD (для порівняння з рядками моделі). */
  explorerByService?: Record<string, number>;
};

export function loadPersistedUi(): Partial<PersistedUi> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as unknown;
    if (!data || typeof data !== "object") return null;
    return data as Partial<PersistedUi>;
  } catch {
    return null;
  }
}

export function savePersistedUi(state: PersistedUi): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota / private mode */
  }
}

export function normalizeCostDisplayUnit(x: unknown): CostDisplayUnit {
  if (x === "year" || x === "perDau" || x === "perMillionReq") return x;
  return "month";
}

export function normalizeScenarioNotes(x: unknown): string | undefined {
  if (typeof x !== "string") return undefined;
  return x.slice(0, 4000).trim() || undefined;
}

/** Last Cost Explorer by-service import (capped). */
export function normalizeExplorerByService(
  x: unknown,
): Record<string, number> | undefined {
  if (!x || typeof x !== "object") return undefined;
  const o = x as Record<string, unknown>;
  const out: Record<string, number> = {};
  let n = 0;
  for (const [k, v] of Object.entries(o)) {
    if (n >= 60) break;
    const key = k.slice(0, 96).trim();
    if (!key) continue;
    const num = Number(v);
    if (!Number.isFinite(num) || num < 0) continue;
    out[key] = Math.round(num * 100) / 100;
    n++;
  }
  return Object.keys(out).length ? out : undefined;
}
